import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, inputCls, Card } from "@/components/cc";
import { Link2, Trash2, ExternalLink, FileText, Quote, RotateCcw } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const RELATIONSHIPS = ["supporting_evidence", "context", "background", "dispute", "correction", "related"];
const pathFor = (t) => (t === "event" ? "events" : "standings");

// ============ Linking section for Event Detail / Standings Snapshot Detail ============
export function SourceClaimLinker({ targetType, targetId }) {
  const [links, setLinks] = useState([]);
  const [sources, setSources] = useState([]);
  const [claims, setClaims] = useState([]);
  const [kind, setKind] = useState("source");
  const [linkedId, setLinkedId] = useState("");
  const [rel, setRel] = useState("supporting_evidence");
  const [note, setNote] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/live-data/${pathFor(targetType)}/${targetId}/links`).then((r) => setLinks(r.data)).catch(() => {});
  }, [targetType, targetId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/sources").then((r) => setSources(r.data)).catch(() => {});
    api.get("/claims").then((r) => setClaims(r.data)).catch(() => {});
  }, []);

  const pool = kind === "source" ? sources : claims;
  const labelOf = (r) => (kind === "source" ? r.title : r.statement);
  const filtered = pool.filter((r) => !q || (labelOf(r) || "").toLowerCase().includes(q.toLowerCase()));

  const add = async () => {
    if (!linkedId) { toast.error("Select a record to link"); return; }
    setBusy(true);
    try {
      await api.post(`/live-data/${pathFor(targetType)}/${targetId}/links`, { linked_type: kind, linked_id: linkedId, relationship: rel, note: note || undefined });
      toast.success(`${pretty(kind)} linked`);
      setLinkedId(""); setNote(""); setQ(""); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (lid) => {
    try { await api.delete(`/live-data/${pathFor(targetType)}/${targetId}/links/${lid}`); toast.success("Link removed"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const updateNote = async (lid, relationship, noteVal) => {
    try { await api.patch(`/live-data/${pathFor(targetType)}/${targetId}/links/${lid}`, { relationship, note: noteVal }); toast.success("Link updated"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="border border-zinc-800 rounded-sm p-3 space-y-3" data-testid={`ld-sc-links-${targetType}`}>
      <div className="flex items-center gap-2"><Link2 className="h-4 w-4 text-emerald-400" /><span className="text-sm font-semibold text-white">Sources & Claims</span><span className="text-xs text-zinc-500">({links.length})</span></div>
      <p className="text-[0.65rem] text-zinc-600">Link EXISTING Source Library / Claims &amp; Evidence records by reference. Nothing is copied; the linked record is read live. Removing a link never deletes the record. Links stay with this normalized event across Schedules / Live / Results views.</p>

      {/* linked list */}
      <div className="space-y-1.5">
        {links.length === 0 && <p className="text-xs text-zinc-600" data-testid="ld-sc-empty">No sources or claims linked yet.</p>}
        {links.map((l) => (
          <div key={l.id} data-testid={`ld-sc-link-${l.id}`} className="flex items-start justify-between gap-2 border-b border-zinc-900 pb-1.5">
            <div className="text-xs min-w-0">
              <div className="flex items-center gap-1.5">
                {l.linked_type === "source" ? <FileText className="h-3.5 w-3.5 text-sky-400 shrink-0" /> : <Quote className="h-3.5 w-3.5 text-violet-400 shrink-0" />}
                <span className="text-zinc-200 font-medium truncate" data-testid={`ld-sc-title-${l.id}`}>{l.record?.title || <span className="text-red-400">record unavailable</span>}</span>
              </div>
              <div className="text-zinc-500 mt-0.5 flex flex-wrap gap-x-2">
                <span>{pretty(l.linked_type)}</span>
                {l.record?.verdict && <span>· verdict: <b className="text-zinc-300">{pretty(l.record.verdict)}</b></span>}
                {l.record?.status && !l.record?.verdict && <span>· status: <b className="text-zinc-300">{pretty(l.record.status)}</b></span>}
                {l.record?.is_private && <span className="text-red-300">· private</span>}
                <span>· rel: <b className="text-zinc-300">{pretty(l.relationship)}</b></span>
              </div>
              {l.note && <div className="text-zinc-500 italic mt-0.5">“{l.note}”</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {l.linked_type === "claim" && l.record && <Link to={`/command-center/claims/${l.record.id}`} data-testid={`ld-sc-open-${l.id}`} className="text-sky-400 hover:text-sky-300"><ExternalLink className="h-3.5 w-3.5" /></Link>}
              {l.linked_type === "source" && l.record?.url && <a href={l.record.url} target="_blank" rel="noreferrer" data-testid={`ld-sc-open-${l.id}`} className="text-sky-400 hover:text-sky-300"><ExternalLink className="h-3.5 w-3.5" /></a>}
              <button data-testid={`ld-sc-remove-${l.id}`} onClick={() => remove(l.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* add form */}
      <div className="grid md:grid-cols-2 gap-2 pt-1">
        <Field label="Type">
          <select data-testid="ld-sc-kind" className={inputCls} value={kind} onChange={(e) => { setKind(e.target.value); setLinkedId(""); }}>
            <option value="source">Source Library</option>
            <option value="claim">Claims & Evidence</option>
          </select>
        </Field>
        <Field label="Relationship">
          <select data-testid="ld-sc-relationship" className={inputCls} value={rel} onChange={(e) => setRel(e.target.value)}>
            {RELATIONSHIPS.map((r) => <option key={r} value={r}>{pretty(r)}</option>)}
          </select>
        </Field>
        <Field label={`Search ${kind === "source" ? "sources" : "claims"}`}><input data-testid="ld-sc-search" className={inputCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by text…" /></Field>
        <Field label="Select record">
          <select data-testid="ld-sc-linked-select" className={inputCls} value={linkedId} onChange={(e) => setLinkedId(e.target.value)}>
            <option value="">Select…</option>
            {filtered.map((r) => <option key={r.id} value={r.id}>{(labelOf(r) || "").slice(0, 70)}</option>)}
          </select>
        </Field>
        <Field label="Relationship note (optional, private)"><input data-testid="ld-sc-note" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why is this relevant?" /></Field>
        <div className="flex items-end"><Btn data-testid="ld-sc-add" onClick={add} disabled={busy || !linkedId} className="text-xs py-1.5">{busy ? "Linking…" : "Link record"}</Btn></div>
      </div>
      {pool.length === 0 && <p className="text-[0.65rem] text-zinc-600">No {kind === "source" ? "sources" : "claims"} exist yet. Create them in the {kind === "source" ? "Source Library" : "Claims & Evidence"} first — nothing is auto-created here.</p>}
    </div>
  );
}

// ============ Reciprocal read-only display for Source / Claim detail ============
export function LiveDataReciprocal({ kind, id }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    if (!id) return;
    api.get(`/live-data/for-${kind}/${id}`).then((r) => setRows(r.data)).catch(() => {});
  }, [kind, id]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card className="p-4" data-testid={`ld-reciprocal-${kind}`}>
      <div className="label-caps text-zinc-500 mb-3 flex items-center gap-2"><Link2 className="h-3.5 w-3.5 text-emerald-400" /> Linked Sports Live Data</div>
      {rows.length === 0 && <p className="text-xs text-zinc-600" data-testid={`ld-reciprocal-empty-${kind}`}>Not linked to any live-data event or standings snapshot.</p>}
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.link_id} data-testid={`ld-recip-${r.link_id}`} className="text-xs border-b border-zinc-900 pb-1.5">
            <div className="flex items-center gap-1.5">
              <span className="px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-300 text-[0.6rem]">{r.target_type === "standings" ? "Standings" : "Event"}</span>
              <span className="text-zinc-200 font-medium">{r.available ? r.title : <span className="text-red-400">record unavailable</span>}</span>
            </div>
            {r.available && <div className="text-zinc-500 mt-0.5 flex flex-wrap gap-x-2">
              <span>{r.competition?.name}</span>
              {r.season && <span>· {r.season}</span>}
              {r.status && <span>· {pretty(r.status)}</span>}
              {r.standings_type && <span>· {pretty(r.standings_type)}</span>}
              <span>· rel: <b className="text-zinc-300">{pretty(r.relationship)}</b></span>
            </div>}
            <div className="text-[0.6rem] text-zinc-600 mt-0.5">Linked {r.linked_at ? new Date(r.linked_at).toLocaleString() : "—"}{r.note ? ` · “${r.note}”` : ""}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}
